"use client"
import { useState } from "react"
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Avatar,
} from "@mui/material"
import { Add, Edit, Delete, Visibility, People, MedicalServices, Work } from "@mui/icons-material"
import { useApp } from "../contexts/AppContext"

export default function StaffManagement({ user }) {
  const { state, actions } = useApp()

  const [open, setOpen] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    department: "",
    specialization: "",
    phone: "",
    email: "",
    address: "",
    salary: 0,
    shift: "",
    experience: "",
  })

  const roles = ["Doctor", "Nurse", "Technician", "Pharmacist", "Receptionist", "Administrator", "Security", "Cleaner"]
  const departments = [
    "Cardiology",
    "Neurology",
    "Orthopedics",
    "Pediatrics",
    "Emergency",
    "Laboratory",
    "Pharmacy",
    "Administration",
  ]
  const shifts = ["Morning", "Evening", "Night", "Rotating"]

  const handleOpen = (staffMember = null) => {
    if (staffMember) {
      setSelectedStaff(staffMember)
      setFormData({
        name: staffMember.name,
        role: staffMember.role,
        department: staffMember.department,
        specialization: staffMember.specialization,
        phone: staffMember.phone,
        email: staffMember.email,
        address: staffMember.address,
        salary: staffMember.salary,
        shift: staffMember.shift,
        experience: staffMember.experience,
      })
    } else {
      setSelectedStaff(null)
      setFormData({
        name: "",
        role: "",
        department: "",
        specialization: "",
        phone: "",
        email: "",
        address: "",
        salary: 0,
        shift: "",
        experience: "",
      })
    }
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setSelectedStaff(null)
  }

  const handleSave = () => {
    if (selectedStaff) {
      actions.updateStaff({ ...selectedStaff, ...formData })
    } else {
      actions.addStaff({
        ...formData,
        joinDate: new Date().toISOString().split("T")[0],
        status: "Active",
        employeeId: `EMP${(state.staff.length + 1).toString().padStart(3, "0")}`,
      })
    }
    handleClose()
  }

  const handleDelete = (id) => {
    actions.deleteStaff(id)
  }

  const handleStatusChange = (id, status) => {
    const staff = state.staff.find((s) => s.id === id)
    if (staff) {
      actions.updateStaff({ ...staff, status })
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "success"
      case "Inactive":
        return "error"
      case "On Leave":
        return "warning"
      default:
        return "default"
    }
  }

  const getRoleIcon = (role) => {
    switch (role) {
      case "Doctor":
        return <MedicalServices />
      case "Nurse":
        return <People />
      default:
        return <Work />
    }
  }

  const totalStaff = state.staff.length
  const activeStaff = state.staff.filter((s) => s.status === "Active").length
  const doctors = state.staff.filter((s) => s.role === "Doctor").length
  const nurses = state.staff.filter((s) => s.role === "Nurse").length

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Staff Management</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
          Add New Staff
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <People sx={{ fontSize: 40, color: "primary.main", mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Staff
                  </Typography>
                  <Typography variant="h4">{totalStaff}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Staff
              </Typography>
              <Typography variant="h4" color="success.main">
                {activeStaff}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <MedicalServices sx={{ fontSize: 40, color: "info.main", mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Doctors
                  </Typography>
                  <Typography variant="h4">{doctors}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Nurses
              </Typography>
              <Typography variant="h4" color="secondary.main">
                {nurses}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Staff</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Specialization</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Shift</TableCell>
              <TableCell>Experience</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.staff.map((staffMember) => (
              <TableRow key={staffMember.id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>{getRoleIcon(staffMember.role)}</Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {staffMember.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {staffMember.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{staffMember.role}</TableCell>
                <TableCell>{staffMember.department}</TableCell>
                <TableCell>{staffMember.specialization}</TableCell>
                <TableCell>{staffMember.phone}</TableCell>
                <TableCell>{staffMember.shift}</TableCell>
                <TableCell>{staffMember.experience}</TableCell>
                <TableCell>
                  <Chip label={staffMember.status} color={getStatusColor(staffMember.status)} size="small" />
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton size="small">
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpen(staffMember)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(staffMember.id)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Staff Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{selectedStaff ? "Edit Staff Member" : "Add New Staff Member"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  label="Role"
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  {roles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={formData.department}
                  label="Department"
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Specialization"
                value={formData.specialization}
                onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Salary (₹)"
                type="number"
                value={formData.salary}
                onChange={(e) => setFormData({ ...formData, salary: Number.parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Shift</InputLabel>
                <Select
                  value={formData.shift}
                  label="Shift"
                  onChange={(e) => setFormData({ ...formData, shift: e.target.value })}
                >
                  {shifts.map((shift) => (
                    <MenuItem key={shift} value={shift}>
                      {shift}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Experience"
                value={formData.experience}
                onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {selectedStaff ? "Update" : "Add"} Staff
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
